import { Util } from '../../util/Util';
import type { RecentTransaction } from './DashboardContext';

export const formatDashboardDate = (d: Date): string => Util.formatISODate(d);

export const parseDashboardDateInput = (value: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const [, year, month, day] = match.map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatRecentTransactionDate = (dateStr: string): string => {
    if (!dateStr) return '—';
    const dateOnly = dateStr.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
        const [year, month, day] = dateOnly.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return dateStr;
    return parsed.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const formatRecentTransactionTimestamp = (dateStr?: string): string => {
    if (!dateStr) return '—';
    const normalized = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T');
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return dateStr;
    return parsed.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const txDate = (row: RecentTransaction): string => row.date.slice(0, 10);

export const sortRecentTransactions = (rows: RecentTransaction[]): RecentTransaction[] =>
    [...rows].sort((a, b) => {
        const dateCmp = txDate(b).localeCompare(txDate(a));
        if (dateCmp !== 0) return dateCmp;
        return b.reference.localeCompare(a.reference);
    });

const mapRecentMeta = (row: Record<string, unknown>) => ({
    depot: (row.depot as string) || '',
    user: (row.user as string) || '',
    createdAt: (row.createdAt as string) || (row.created as string) || '',
    updatedAt: (row.updatedAt as string) || (row.updated as string) || '',
    batch: String(row.batch || row.id || ''),
});

export const mapSaleTransaction = (s: any): RecentTransaction => ({
    id: String(s.batch || s.id || ''),
    type: 'sale',
    reference: s.reference || s.batch || '',
    date: s.date || '',
    amount: Number(s.total) || Number(s.amount) || 0,
    description: s.client || s.receiver_name || s.item_name || '',
    ...mapRecentMeta(s),
});

export const mapPaymentTransaction = (p: any): RecentTransaction => ({
    id: String(p.id || p.batch || ''),
    type: 'payment',
    reference: p.reference || p.batch || String(p.id || ''),
    date: p.date || p.pay_date || '',
    amount: Number(p.amount) || 0,
    description: [p.client, p.mode, p.x_type].filter(Boolean).join(' · ') || p.description || '',
    ...mapRecentMeta(p),
});

export const mapSupplyTransaction = (s: any): RecentTransaction => ({
    id: String(s.batch || s.id || ''),
    type: 'supply',
    reference: s.reference || s.batch || '',
    date: s.date || '',
    amount: Number(s.total) || Number(s.amount) || 0,
    description: s.supplier || s.supplier_name || s.description || s.item_name || '',
    ...mapRecentMeta(s),
});

const classifyStockTransactionType = (row: any): RecentTransaction['type'] => {
    const type = String(row.type || '').toLowerCase();
    if (type === 'discard' || type === 'restore' || type === 'inventory' || type === 'movement') {
        return type;
    }
    const operation = String(row.operation || '').toLowerCase();
    if (operation.includes('discard')) return 'discard';
    if (operation.includes('restore')) return 'restore';
    if (operation.includes('inventor')) return 'inventory';
    return 'movement';
};

export const mapMovementTransaction = (m: any): RecentTransaction => ({
    id: String(m.batch || m.id || ''),
    type: classifyStockTransactionType(m),
    reference: m.reference || m.batch || '',
    date: m.date || '',
    amount: Number(m.total) || Number(m.amount) || 0,
    description: m.description || [m.operation, m.item_name].filter(Boolean).join(': ') || '',
    ...mapRecentMeta(m),
});

export const mapConversionTransaction = (c: any): RecentTransaction => ({
    id: String(c.batch || c.id || ''),
    type: 'conversion',
    reference: c.reference || c.batch || '',
    date: c.date || '',
    amount: Number(c.total_amt) || Number(c.total) || Number(c.amount) || 0,
    description: c.description || [c.source_item, c.dest_item].filter(Boolean).join(' → ') || '',
    ...mapRecentMeta(c),
});

export const mapProductionTransaction = (p: any): RecentTransaction => ({
    id: String(p.batch || p.id || ''),
    type: 'production',
    reference: p.reference || p.batch || '',
    date: p.date || '',
    amount: Number(p.output_amt) || Number(p.amount) || 0,
    description: p.description || p.bom_name || '',
    ...mapRecentMeta(p),
});

export const mapBomTransaction = (b: any): RecentTransaction => ({
    id: String(b.id || ''),
    type: 'bom',
    reference: b.code || b.reference || b.name || String(b.id || ''),
    date: b.date || b.created || '',
    amount: Number(b.output_amt) || Number(b.amount) || 0,
    description: b.description || b.name || '',
    ...mapRecentMeta(b),
});